import { css, styled } from '../../../common.styles';
import { baseColor, mobile, normalFont, primaryFont, tablet } from '../../../styles/general/theme';
import React from 'react';
import { ButtonProps } from '../general/common-button';

const BaseStyle = css<{ fullWidth?: boolean; width?: string }>`
  font-family: ${primaryFont};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  text-transform: uppercase;
  padding: 12px 20px;
  cursor: pointer;
  user-select: none;
  width: fit-content;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  text-decoration: none;
  min-width: max-content;
  border-radius: 6px;

  ${(p) =>
    p.fullWidth &&
    css`
      width: 100%;
      justify-content: center;
    `};

  ${(p) =>
    p.width &&
    css`
      width: ${p.width};
    `};

  @media (max-width: ${tablet}px) {
    justify-content: center;
  }

  @media (max-width: ${mobile}px) {
    justify-content: center;
    width: 100%;
  }
`;

export const AbarthButtonStyle = css<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  variant?: string;
  darkMode?: boolean;
}>`
  ${BaseStyle};

  ${(p) =>
    (p.variant?.toLowerCase() === 'primary' || !p.variant) &&
    css`
      color: ${baseColor(100)};
      background-color: ${baseColor(400)};
      box-shadow: 0 0 5px 0 ${baseColor(410)};
      transition: box-shadow 1s ease;

      :hover {
        box-shadow: 0 0 20px 0 ${baseColor(410)};
      }
    `};

  ${(p) =>
    p.variant?.toLowerCase() === 'secondary' &&
    css`
      color: ${baseColor(900)};
      background-color: ${baseColor(100)};
      box-shadow: 0 0 5px 0 ${baseColor(100)};
      transition: box-shadow 1s ease;

      :hover {
        box-shadow: 0 0 20px 0 ${baseColor(100)};
      }
    `};

  ${(p) =>
    p.variant?.toLowerCase() === 'tertiary' &&
    css`
      color: ${baseColor(100)};
      background-color: ${baseColor(900)};
      box-shadow: 0 0 5px 0 ${baseColor(900)};
      transition: box-shadow 1s ease;

      :hover {
        box-shadow: 0 0 20px 0 ${baseColor(900)};
      }
    `};
`;

export const DisabledAbarthStyle = css<ButtonProps>`
  ${(p) =>
    (p.variant?.toLowerCase() === 'primary' ||
      p.variant?.toLowerCase() === 'secondary' ||
      p.variant?.toLowerCase() === 'tertiary') &&
    p.disabled &&
    css`
      cursor: not-allowed;
      border-color: ${baseColor(190)};
      color: ${baseColor(185)};
      background-color: ${baseColor(190)};
      box-shadow: none;
      &:hover {
        color: ${baseColor(185)};
        background-color: ${baseColor(120)};
        border-color: ${baseColor(180)};
        box-shadow: none;
      }
    `};
`;

export const AbarthMainButton = styled.a<ButtonProps>`
  ${AbarthButtonStyle};
  ${DisabledAbarthStyle};
`;

export const AbarthChevronLeftArrowIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_8875_8247)">
      <path d="M13.6 16L18.3 11.3L17.5 10.5L12 16L17.5 21.5L18.3 20.7L13.6 16Z" fill="#333333" />
    </g>
    <defs>
      <clipPath id="clip0_8875_8247">
        <rect width="32" height="32" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const AbarthChevronRightArrowIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_8875_8248)">
      <path d="M18.2 16L13.5 20.7L14.3 21.5L19.8 16L14.3 10.5L13.5 11.3L18.2 16Z" fill="#333333" />
    </g>
    <defs>
      <clipPath id="clip0_8875_8248">
        <rect width="32" height="32" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const AbarthArrowAndTextButtonWrapper = styled.a<ButtonProps>`
  font-family: ${normalFont};
  letter-spacing: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${baseColor(100)};

  svg {
    path {
      fill: ${baseColor(100)};

      ${(p) =>
        (p.darkMode || p.darkBackground) &&
        css`
          fill: ${baseColor(900)};
        `};
    }
  }
`;

const AbarthTextButtonWrapper = styled.a<ButtonProps>`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  text-transform: uppercase;
  color: ${baseColor(100)};
  text-decoration: underline;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    text-decoration-line: none;
    color: ${baseColor(100)};
    ${(p) =>
      (p.darkMode || p.darkBackground) &&
      css`
        color: ${baseColor(900)};
      `};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(190)};
      &:hover {
        color: ${baseColor(190)}!important;
        text-decoration: underline;
      }
    `};

  ${(p) =>
    (p.darkMode || p.darkBackground) &&
    css`
      color: ${baseColor(900)};

      &:hover {
        text-decoration-line: none;
        color: ${baseColor(900)};
    `};
`;

export const AbarthArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <AbarthArrowAndTextButtonWrapper {...props}>
      {position === 'left' && <AbarthChevronLeftArrowIcon />}
      {props.children}
      {position === 'right' && <AbarthChevronRightArrowIcon />}
    </AbarthArrowAndTextButtonWrapper>
  );
};

export const AbarthButton: React.FC<ButtonProps> = (props: ButtonProps) => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <AbarthMainButton {...props} />;
    case 'secondary':
      return <AbarthMainButton {...props} />;
    case 'tertiary':
      return <AbarthMainButton {...props} />;
    case 'quaternary':
      return <AbarthTextButtonWrapper {...props} />;
    case 'icon':
      return <AbarthArrowAndTextButton {...props} />;
    default:
      return null;
  }
};
